import { useEffect, useMemo } from 'react'
import { InterviewStatus } from '@bosmart/types'
import { formatTime } from './interview-session/format'
import { useEndInterview } from './interview-session/useEndInterview'
import { useLiveSession } from './interview-session/useLiveSession'
import { useMediaInitialization } from './interview-session/useMediaInitialization'
import { useNotification } from './interview-session/useNotification'
import { useScreenShareControls } from './interview-session/useScreenShareControls'
import { useSessionEffects } from './interview-session/useSessionEffects'
import { useSessionRefs } from './interview-session/useSessionRefs'
import { useSessionTimer } from './interview-session/useSessionTimer'
import { useSessionUiState } from './interview-session/useSessionUiState'
import { useStopAllStreams } from './interview-session/useStopAllStreams'
import { useThankYouRedirect } from './interview-session/useThankYouRedirect'
import { useVideoStreaming } from './interview-session/useVideoStreaming'
import type {
  InterviewSessionOptions,
  SessionSetters,
  SessionUiState,
  UseInterviewSessionResult,
} from './interview-session/types'

export function useInterviewSession({
  interviewId,
  token,
  otpProofToken,
  onEnd,
}: InterviewSessionOptions): UseInterviewSessionResult {
  const apiBaseUrl = useMemo(
    () =>
      import.meta.env.VITE_API_URL ||
      (typeof window !== 'undefined' && import.meta.env.DEV
        ? 'http://localhost:3002'
        : typeof window !== 'undefined'
          ? window.location.origin
          : 'http://localhost:3002'),
    []
  )

  const refs = useSessionRefs()
  const ui = useSessionUiState()
  const { state, setters } = splitUiState(ui)
  const showNotification = useNotification(refs, setters.setNotification)
  const stopAllStreams = useStopAllStreams(refs, setters.setIsTimerRunning)
  const startVideoStreaming = useVideoStreaming(refs)

  useThankYouRedirect(state.showThankYou)
  useSessionTimer(state.isTimerRunning, setters.setDuration)
  useMediaInitialization({
    refs,
    setStatus: setters.setStatus,
    setError: setters.setError,
  })

  const { startInterview } = useLiveSession({
    refs,
    state,
    setters,
    interviewId,
    token,
    otpProofToken,
    apiBaseUrl,
    showNotification,
    startVideoStreaming,
  })

  const { toggleMute, toggleScreenShare } = useScreenShareControls({
    refs,
    interviewId,
    apiBaseUrl,
    isScreenSharing: state.isScreenSharing,
    setIsScreenSharing: setters.setIsScreenSharing,
    setIsMuted: setters.setIsMuted,
    showNotification,
  })

  const confirmEndSession = useEndInterview({
    refs,
    state,
    setters,
    interviewId,
    token,
    onEnd,
    stopAllStreams,
    showNotification,
  })

  useSessionEffects({ refs, state, setters, stopAllStreams })
  useRestartVideoStreaming(
    refs.serviceRef.current !== null,
    state.isScreenSharing,
    state.status,
    startVideoStreaming
  )

  return {
    refs,
    state: {
      ...state,
      formattedDuration: formatTime(state.duration),
      hasLiveService: refs.serviceRef.current !== null,
    },
    actions: {
      startInterview,
      toggleMute,
      toggleScreenShare,
      requestEndSession: () => setters.setShowConfirmation(true),
      cancelEndSession: () => setters.setShowConfirmation(false),
      confirmEndSession,
      reload: () => window.location.reload(),
    },
  }
}

function splitUiState(ui: ReturnType<typeof useSessionUiState>): {
  state: SessionUiState
  setters: SessionSetters
} {
  const {
    setStatus,
    setAiSpeaking,
    setTranscription,
    setError,
    setNotification,
    setIsScreenSharing,
    setIsMuted,
    setHistory,
    setModelActive,
    setUserActive,
    setHasGreetingReceived,
    setShowStartCue,
    setShowSpeakFirstPrompt,
    setIsCallEnding,
    setShowConfirmation,
    setShowThankYou,
    setDuration,
    setIsTimerRunning,
    ...state
  } = ui

  return {
    state,
    setters: {
      setStatus,
      setAiSpeaking,
      setTranscription,
      setError,
      setNotification,
      setIsScreenSharing,
      setIsMuted,
      setHistory,
      setModelActive,
      setUserActive,
      setHasGreetingReceived,
      setShowStartCue,
      setShowSpeakFirstPrompt,
      setIsCallEnding,
      setShowConfirmation,
      setShowThankYou,
      setDuration,
      setIsTimerRunning,
    },
  }
}

function useRestartVideoStreaming(
  hasLiveService: boolean,
  isScreenSharing: boolean,
  status: InterviewStatus,
  startVideoStreaming: () => void
) {
  useEffect(() => {
    if (status === InterviewStatus.CONNECTED && hasLiveService) {
      startVideoStreaming()
    }
  }, [hasLiveService, isScreenSharing, startVideoStreaming, status])
}
